import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NotifcationProps, Notification } from '@components/base/overlays/Notifications';

type UniversalModalContextType = {
    open: boolean;
    component?: React.ReactNode;
};

type UniversalModalDispatchType = {
    toggle: (val: boolean, component?: React.ReactNode) => void;
    dispatchNotification: (props: NotifcationProps) => void;
};

const UniversalModalContext = React.createContext<UniversalModalContextType | null>(null);
export const UniversalModalDispatchContext = React.createContext<UniversalModalDispatchType | null>(
    null,
);

export function UniversalModalProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<UniversalModalContextType>({
        open: false,
    });
    const timer = useRef<null | NodeJS.Timeout>(null);
    const [notifcationProps, setProps] = useState<NotifcationProps & { show: boolean }>({
        title: '',
        text: '',
        type: 'danger',
        show: false,
    });

    const dispatch = useMemo(
        () => ({
            toggle: (val: boolean, component?: React.ReactNode) => {
                setState({ open: val, component });
            },
            dispatchNotification: (props: NotifcationProps) => {
                setProps({
                    ...props,
                    show: true,
                });
            },
        }),
        [],
    );
    const handleShow = useCallback(() => {
        setProps((s) => ({ ...s, show: false }));
    }, [setProps]);

    useEffect(() => {
        if (notifcationProps.show && timer.current === null) {
            timer.current = setTimeout(() => {
                setProps((s) => ({ ...s, show: false }));
                timer.current = null;
            }, 6000);
        }
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
    }, [setProps, notifcationProps]);

    return (
        <UniversalModalContext.Provider value={state}>
            <UniversalModalDispatchContext.Provider value={dispatch}>
                <Notification {...notifcationProps} setShow={handleShow} />
                {children}
            </UniversalModalDispatchContext.Provider>
        </UniversalModalContext.Provider>
    );
}

export function useUniversalModalState() {
    const context = useContext(UniversalModalContext);
    if (context === null) {
        throw new Error('Expected Context for <Sidebar /> to be Initialized');
    }
    return context;
}

/**
 * This component right now is for Form Components and not the individual confirmation of actions modal
 * see Modal from @components/base
 *
 * @returns
 */
export function useUniversalModalDispatch() {
    const context = useContext(UniversalModalDispatchContext);
    if (context === null) {
        throw new Error('Expected Dispatch Context for <Sidebar /> to be Initialized');
    }

    return context;
}
